/**
 * A minimal client-side router: `history.pushState`/`popstate`, an auth guard, and a
 * capability-based default landing route.
 *
 * Built now, per `plan/identity-and-portal.md`'s migration order (step 3), while there is
 * exactly one route to move (the VR session) rather than after several features have each
 * bolted their own ad hoc thing onto boot the way the VR session used to be the entire app.
 *
 * Deliberately small: no path parameters, no wildcard segments, no nested routes. Every
 * registered path is matched by exact string equality against `location.pathname`. That is
 * also what keeps the deep-link trap this project already shipped once out of reach rather
 * than merely patched — `Program.cs`'s `MapFallbackToFile("index.html")` relies on
 * `MapFallbackToFile`'s own default `{*path:nonfile}` pattern, which does not match a path
 * containing a dot (see `Program.cs`'s own comment on the `.glb` content-type bug, the
 * identical failure mode). Two ways to avoid that trap were on the table
 * (`plan/identity-and-portal.md`, "The portal is capability-routed") — keep dots out of
 * portal URLs by design, or change the fallback pattern and test it — and this router chose
 * the first: every route below is a bare path segment (`/portal`, `/room`), nothing ever
 * builds a path from a filename, export id or anything else that could carry a dot, so the
 * server-side fallback pattern was left untouched.
 *
 * **Capability is not intent.** `resolveDefault` is consulted only for the bare `/` path —
 * a direct request for a named route (typed in the address bar, reached via `navigate`, or
 * restored from browser history) is never redirected because of what a capability check
 * says. `XrManager.isSupported()` (promoted from `XrManager.ts`'s own
 * `navigator.xr.isSessionSupported('immersive-vr')` check) answers "can this device do VR",
 * not "does the person at this device want VR right now" — a desktop with a tethered headset
 * reports `true` and still wants the portal by default sometimes. Picking a default landing
 * is the only thing capability is allowed to influence.
 *
 * **The auth guard redirects; it does not let an unauthenticated request through to see a
 * route.** `guard`, if configured, is asked before *every* navigation, including the initial
 * one. Returning `false` calls `onUnauthorized(path)` and stops there — the requested route's
 * handler never runs, and the URL is never updated to point at it. The real implementation
 * (`main.ts`, via `AccountGate.ensureSignedIn`) shows the sign-in screen and calls
 * `router.navigate(path)` again once it resolves; nothing about that retry is `Router`'s own
 * concern, which is what keeps this class testable with a fake guard and no real network call.
 */

/**
 * The pathname half of a path-with-query string, e.g. `/room?roomId=X` -> `/room`. Route
 * matching (`resolvePath`/the handler lookup in `render`) always goes through this — routes
 * are registered by bare pathname (`RouterConfig.routes`'s own doc comment: "no path
 * parameters, no wildcards"), and a query string is how a caller like `PortalHome`'s room
 * links thread an id through without Router ever having to know about it. Found missing
 * entirely (`plan/identity-and-portal.md`'s own room-switching task): `navigate('/room?roomId=X')`
 * was being looked up verbatim against `{'/room': ...}`, matched nothing, and silently fell
 * through to `fallback` — so every "enter this room" link from the portal actually rendered
 * the fallback route instead, with no test ever having exercised a query string to catch it.
 */
function pathnameOf(raw: string): string {
  return raw.split('?')[0]!.split('#')[0]!;
}

export type RouteHandler = (
  mount: HTMLElement,
) => void | (() => void) | Promise<void | (() => void)>;

export interface RouterConfig {
  /** Where each route's own content is mounted. Cleared (via the previous route's own
   *  returned cleanup, if any) before the next route renders into it. */
  mount: HTMLElement;

  /** Exact-path routes. No parameters, no wildcards — see this module's own doc comment for
   *  why that is deliberate rather than a limitation to grow out of later. */
  routes: Record<string, RouteHandler>;

  /**
   * Asked before every navigation, including the very first one. `false` means "do not
   * render this route" — the router calls `onUnauthorized` instead of `routes[path]`, and
   * neither the mount nor the URL changes.
   */
  guard?: (path: string) => boolean | Promise<boolean>;

  /** Called in place of rendering, whenever `guard` refuses a path. */
  onUnauthorized?: (path: string) => void;

  /**
   * Resolves what the bare `/` path actually means, computed fresh on every visit to `/` —
   * this is the one seam capability detection is allowed to reach through. Never consulted
   * for a direct request to a named route, so a capability check can pick a default without
   * ever making the other route unreachable.
   */
  resolveDefault?: () => string | Promise<string>;

  /** Where an unrecognised path (not `/`, not in `routes`) resolves to instead — a stale or
   *  mistyped link stays inside the app rather than rendering nothing. */
  fallback?: string;
}

export class Router {
  private currentCleanup: (() => void) | null = null;
  private currentPath = '';
  private readonly popStateHandler = () => void this.render(window.location.pathname, false);

  /**
   * Guards against overlapping navigations. `/room`'s own route handler is not a quick
   * render — it is the entire former composition-root boot (hub connect, model preload, XR
   * setup) — so a navigation started while a previous one is still awaiting its handler is a
   * real scenario, not a hypothetical one: the "Portal" link on the VR entry screen is
   * reachable the instant the screen exists, long before that boot finishes. Without this,
   * the earlier call's own `await handler(...)` would resume after the later one has already
   * rendered, and unconditionally overwrite `currentCleanup`/`currentPath` with its own —
   * stale — results, corrupting state for a route that is no longer the one on screen.
   * Incremented on every `render()` call; a call whose token no longer matches by the time
   * its handler resolves knows it was superseded, disposes whatever it just built (nobody
   * else will), and touches nothing else.
   */
  private renderToken = 0;

  constructor(private readonly config: RouterConfig) {
    window.addEventListener('popstate', this.popStateHandler);
  }

  /** The path currently rendered — not necessarily `location.pathname` mid-render, and never
   *  the path of a navigation the guard refused. */
  get path(): string {
    return this.currentPath;
  }

  /** Renders whatever `location.pathname` already is. Call once, at boot. */
  async start(): Promise<void> {
    await this.render(window.location.pathname, false);
  }

  /** Navigates in-app: pushes history and renders, subject to the same guard `start` uses. */
  async navigate(path: string): Promise<void> {
    await this.render(path, true);
  }

  /** Stops listening for `popstate`. The last-rendered route's own cleanup is not run here —
   *  callers that also want that should call it themselves via whatever they kept from
   *  `navigate`/`start`, the same way any other resource owner in this project does. */
  dispose(): void {
    window.removeEventListener('popstate', this.popStateHandler);
  }

  private async render(requestedPath: string, push: boolean): Promise<void> {
    const token = ++this.renderToken;

    const resolvedPath = await this.resolvePath(requestedPath);

    if (this.config.guard) {
      const allowed = await this.config.guard(resolvedPath);

      if (!allowed) {
        // A superseded, refused navigation has nothing to undo — onUnauthorized is a UI
        // side effect (main.ts shows sign-in), not app state this router owns, so it is not
        // worth suppressing even if a newer navigation has since started. Only the state
        // this class itself owns (currentCleanup/currentPath, the URL) is guarded below.
        this.config.onUnauthorized?.(resolvedPath);
        return;
      }
    }

    const handler = this.config.routes[pathnameOf(resolvedPath)];

    if (!handler) {
      // Neither a known route nor something resolvePath already redirected — nothing left
      // to render. Left as a console warning rather than a throw: a bad link should not take
      // the whole app down with it.
      console.warn(`Router: no route registered for '${resolvedPath}'.`);
      return;
    }

    // Compared against the full pathname+search, not pathname alone — resolvedPath can carry
    // a query string (see pathnameOf's own doc comment) and comparing against pathname alone
    // would push a redundant history entry on every navigation that happens to already be on
    // the right pathname with a different query.
    if (push && resolvedPath !== window.location.pathname + window.location.search) {
      window.history.pushState(null, '', resolvedPath);
    } else if (!push && resolvedPath !== requestedPath) {
      // requestedPath was '/' (or unrecognised) and resolved to something else — replace
      // so the address bar reflects what actually rendered, and back/forward does not
      // bounce between the bare path and its resolution.
      window.history.replaceState(null, '', resolvedPath);
    }

    // The outgoing route is torn down immediately, before the incoming one has necessarily
    // even started building — "I clicked away" should not wait for whatever was already in
    // flight to finish. `currentCleanup` is cleared here rather than left for the token
    // check below because whichever navigation reaches this line first legitimately owns
    // tearing the previous one down; a second, concurrent call finding it already `null` is
    // exactly correct, not a race.
    this.currentCleanup?.();
    this.currentCleanup = null;
    this.currentPath = resolvedPath;

    const cleanup = await handler(this.config.mount);

    if (token !== this.renderToken) {
      // Superseded while this handler was still running — a later navigation already became
      // current (and has already overwritten currentCleanup/currentPath/history with its own
      // results). Nothing here should touch that state; the only thing left to do is dispose
      // whatever this now-discarded handler itself built, since nothing else ever will.
      cleanup?.();
      return;
    }

    this.currentCleanup = cleanup ?? null;
  }

  private async resolvePath(requestedPath: string): Promise<string> {
    if (requestedPath === '/' && this.config.resolveDefault) {
      return this.config.resolveDefault();
    }

    if (pathnameOf(requestedPath) in this.config.routes) {
      return requestedPath;
    }

    return this.config.fallback ?? requestedPath;
  }
}
